/**
 * StylizedSidebarCalloutRenderer - Renders a stylized sidebar callout block
 * Requirements: 3.1, 3.2, 3.3, 3.4, 4.1, 4.2, 4.3, 4.4, 5.1, 5.2, 5.3, 5.4, 5.5, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7
 *
 * Pure presentation component that renders a two-column layout with main content
 * on the left and a sidebar callout on the right within a gray card.
 */

import React, { useMemo } from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  isRichTextContentEmpty,
  useTheme,
  type SidebarCalloutTitleLevel,
} from '@course-authoring/shared-types';

import { useCardBackground } from './SectionSurfaceContext';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { createSidebarCalloutStyles } from './styles/sidebarCallout';
import { RichTextContent } from './RichTextContent';

export interface StylizedSidebarCalloutRendererProps {
  title: JSONContent;
  titleLevel?: SidebarCalloutTitleLevel;
  body: JSONContent;
  calloutTitle: JSONContent;
  calloutBody: JSONContent;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * CSS to remove default paragraph margins from generated HTML
 */
const contentInnerStyles = createRichTextContentResetStyles('ssc-content');

/**
 * StylizedSidebarCalloutRenderer component
 *
 * Features:
 * - Gray background card (blockHoverBackground) - Requirement 3.1
 * - Rounded corners (16px) - Requirement 3.2
 * - 32px padding - Requirement 3.3
 * - NO identifier badge - Requirement 3.4
 * - Desktop main content expands to fill remaining width - Requirement 4.2
 * - Desktop layout uses an 80% container with slice-based main/sidebar proportions - Requirement 4.3
 * - Mobile stacks the main content and callout at 80% width - Requirement 4.4
 * - Main title with selectable H1/H2/H3 styling - Requirement 5.1
 * - Main body below title - Requirement 5.2
 * - TipTap JSONContent to HTML with inline formatting and list support - Requirements 5.3, 6.6
 * - 16px spacing between title and body - Requirement 5.4
 * - Typography from designTokens - Requirement 5.5
 * - Sidebar with darker background - Requirement 6.1
 * - Sidebar with 16px radius - Requirement 6.2
 * - Sidebar with 30px padding - Requirement 6.3
 * - Callout title using body sizing with bold weight - Requirement 6.4
 * - Callout body below callout title - Requirement 6.5
 * - 12px spacing between callout title and body - Requirement 6.7
 */
export function StylizedSidebarCalloutRenderer({
  title,
  titleLevel = 'h2',
  body,
  calloutTitle,
  calloutBody,
  className,
  style,
}: StylizedSidebarCalloutRendererProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const cardBackground = useCardBackground();
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const hasTitle = !isRichTextContentEmpty(title);
  const hasCalloutTitle = !isRichTextContentEmpty(calloutTitle);
  const sidebarCalloutStyles = useMemo(
    () => createSidebarCalloutStyles(tokens, isMobile, titleLevel),
    [tokens, isMobile, titleLevel]
  );

  const titleHtml = useMemo(() => safeGenerateRichTextHTML(title, extensions), [title, extensions]);
  const bodyHtml = useMemo(() => safeGenerateRichTextHTML(body, extensions), [body, extensions]);
  const calloutTitleHtml = useMemo(
    () => safeGenerateRichTextHTML(calloutTitle, extensions),
    [calloutTitle, extensions]
  );
  const calloutBodyHtml = useMemo(
    () => safeGenerateRichTextHTML(calloutBody, extensions),
    [calloutBody, extensions]
  );

  const cardStyles: React.CSSProperties = {
    width: '100%',
    backgroundColor: 'transparent',
    padding: 0,
  };

  return (
    <div
      className={className}
      style={{ ...cardStyles, ...style }}
    >
      <style>{contentInnerStyles}</style>
      {/* Requirement 3.4: NO identifier badge rendered */}
      <div style={sidebarCalloutStyles.layout}>
        {/* Main Content Area - Requirements 4.2, 5.1, 5.2, 5.3, 5.4, 5.5 */}
        <div style={sidebarCalloutStyles.mainContent}>
          {hasTitle && (
            <RichTextContent
              className="ssc-content"
              style={sidebarCalloutStyles.title}
              html={titleHtml}
            />
          )}
          <RichTextContent
            className="ssc-content"
            style={{
              ...sidebarCalloutStyles.body,
              marginTop: hasTitle ? sidebarCalloutStyles.body.marginTop : 0,
            }}
            html={bodyHtml}
          />
        </div>

        {/* Sidebar Callout - Requirements 4.3, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7 */}
        <div
          style={{
            ...sidebarCalloutStyles.sidebar,
            backgroundColor: cardBackground,
            ...(hasCalloutTitle ? {} : sidebarCalloutStyles.sidebarCentered),
          }}
        >
          {hasCalloutTitle && (
            <RichTextContent
              className="ssc-content"
              style={sidebarCalloutStyles.calloutTitle}
              html={calloutTitleHtml}
            />
          )}
          <RichTextContent
            className="ssc-content"
            style={{
              ...sidebarCalloutStyles.calloutBody,
              marginTop: hasCalloutTitle ? sidebarCalloutStyles.calloutBody.marginTop : 0,
            }}
            html={calloutBodyHtml}
          />
        </div>
      </div>
    </div>
  );
}

StylizedSidebarCalloutRenderer.displayName = 'StylizedSidebarCalloutRenderer';
